import { Position } from '@blueprintjs/core'
import { Menu, MenuDivider, MenuItem } from './menu'
import { Popover } from './popover'
import { capitalCase } from 'change-case'
import { observer } from 'mobx-react-lite'
import React, { ChangeEvent, useState } from 'react'
import { ColumnsIcon } from '../../icons/icons'
import { Column } from '../../types/table'
import { TextInput } from '../inputs/Input'
import { AgGridColumnDef } from '../table2/AgGridTable'
import { TableStore, columnKey } from '../table2/table_store'
import { Button } from './button'
import { Checkbox } from './checkbox'

interface HideColumnsProps<T> {
    columns: Column<T, any>[]
    hiddenColumns: string[]
    setHiddenColumns: (v: string) => void
    className?: string
    menuPopoverPosition?: Position
}

export const HideColumns = <T,>({
    columns,
    hiddenColumns,
    setHiddenColumns,
    className = '',
    menuPopoverPosition = `${Position.BOTTOM}`,
}: HideColumnsProps<T>) => {
    const [search, setSearch] = useState('')

    const cols = columns
        .map((item) => ({
            key: item.key,
            title:
                item.text_title ??
                (typeof item.title == 'string' ? item.title : formatHeader(item.key)),
        }))
        .filter((item) => item.title.toLowerCase().indexOf(search.toLowerCase()) !== -1)

    const menuContent = (
        <Menu
            className="hide-columns overflow-y-auto"
            style={{ maxHeight: '600px', width: '300px' }}
        >
            <div>
                <TextInput
                    autoFocus
                    className="mb-2"
                    value={search}
                    onChange={setSearch}
                    placeholder="Search"
                />
            </div>
            {!!search && (
                <div className="text-gray-300 mb-2 text-xs">
                    Showing {cols.length} / {columns.length} columns
                </div>
            )}
            {!!!search && <div className="text-gray-300 mb-2 text-xs" />}
            {cols.map(({ key, title }, i) => {
                if (!title) {
                    return
                }

                return (
                    <Checkbox
                        key={i}
                        name={key}
                        label={title}
                        className="whitespace-nowrap"
                        onChange={(e: ChangeEvent<any>) => setHiddenColumns(e.target.name)}
                        checked={!hiddenColumns.includes(key)}
                    />
                )
            })}
        </Menu>
    )

    return (
        <Popover content={menuContent} position={menuPopoverPosition} openOnTargetFocus={false}>
            <Button minimal outlined icon={<ColumnsIcon />} className={className} text="Columns" />
        </Popover>
    )
}

interface HideColumnsProps2<T> {
    columns: AgGridColumnDef<T>[]
    className?: string
    menuPopoverPosition?: Position
    store: TableStore<T>
}

/**
 * The segment preview renders one column per metric per lookback period, so
 * `headerName` alone repeats — five columns all read "ACOS". `headerPrefix` is
 * what the grid header shows to tell them apart, so the menu has to carry it
 * too, both as the label and as the text the search box matches.
 */
export const columnMenuLabel = <T,>(item: AgGridColumnDef<T>): string | null => {
    const base =
        item.headerName ??
        item.displayName ??
        (item.field ? capitalCase(item.field as string) : null) ??
        (item.name ? capitalCase(item.name) : null)

    if (!base) {
        return null
    }

    // Preview variables arrive as `___margin`, so the underscore-stripped
    // header name carries leading spaces.
    return (item.headerPrefix ? `${item.headerPrefix} ${base}` : base).trim()
}

export const HideColumns2 = observer(
    <T,>({
        columns,
        className = '',
        menuPopoverPosition = `${Position.BOTTOM}`,
        store,
    }: HideColumnsProps2<T>) => {
        const [search, setSearch] = useState('')

        const cols = columns
            .map((item) => ({
                item,
                key: columnKey(item),
                title: columnMenuLabel(item),
            }))
            .filter((item) => item.title !== null)
            .filter((item) => item.title.toLowerCase().indexOf(search.toLowerCase()) !== -1)

        const menuContent = (
            <Menu
                className="hide-columns overflow-y-auto"
                style={{ maxHeight: '600px', width: '300px' }}
            >
                <div>
                    <TextInput
                        autoFocus
                        className="mb-2"
                        value={search}
                        onChange={setSearch}
                        placeholder="Search"
                    />
                </div>
                {!!search && (
                    <div className="text-gray-300 mb-2 text-xs">
                        Showing {cols.length} / {columns.length} columns
                    </div>
                )}
                {!!!search && <div className="text-gray-300 mb-2 text-xs" />}
                {/*
                 * Acts on the listed columns, not every column, so it agrees
                 * with the count above it: searching narrows what this reveals.
                 * Keeps the menu open, like the checkboxes below it, so the
                 * user can carry on adjusting.
                 */}
                <MenuItem
                    text={search ? 'Restore matching columns' : 'Restore hidden columns'}
                    shouldDismissPopover={false}
                    disabled={!cols.some(({ key }) => store.isUserHidden(key))}
                    onClick={() => store.restoreHiddenColumns(cols.map(({ item }) => item))}
                />
                <MenuDivider />
                {cols.map(({ key, title }, i) => {
                    if (!title) {
                        return
                    }

                    return (
                        <Checkbox
                            key={i}
                            name={key}
                            label={title}
                            className="whitespace-nowrap"
                            onChange={() => store.toggleVisible(key)}
                            checked={!store.isHidden(key)}
                        />
                    )
                })}
            </Menu>
        )

        return (
            <Popover content={menuContent} position={menuPopoverPosition} openOnTargetFocus={false}>
                <Button
                    minimal
                    outlined
                    icon={<ColumnsIcon />}
                    className={className}
                    text="Columns"
                />
            </Popover>
        )
    },
)

export const formatHeader = (name: string) => capitalCase((name ?? '').replace('_', ' '))
